<template>
  <div id="app gifts" class="pt-2">
    <div v-if="loading" class="loading"></div>

    <h3 class="show-sm">Gift Counts</h3>
    <p>
      <strong>THIS IS HIGHLY EXPERIMENTAL AND IN PREVIEW MODE.</strong> This
      page counts the number of gifts a user has in the collection that
      <strong>are not archive locked</strong> – the scraper cannot see things
      that are logged-in. Data like this is harder to scrape! Just like grabbing
      requests, press this button to scrape and wait, then refresh the page:
      <button class="btn btn-sm" @click="scrape">Scrape Gifts</button>. Once the
      data is populated, you can also get it in
      <a :href="`https://autoao3.herokuapp.com/export/exchange/${encodeURIComponent(
        this.exchangeId
      )}/gifts`" target="_blank">CSV</a>.
    </p>
    <p>
      <small>Note that you might have to wait a <em>rather long time</em> for it to
        finish: the scraper has to go participant by participant ():). On the
        upside, this page will update the moment a user is scraped, as opposed
        to all at the end. Please do not spam it, as only one scrape (whether
        for populating requests or updating gift counts!) can happen at a time.
        Take it with a pinch of salt and note the "updated at" column for the
        last time that user's gift page was scraped (local time). The app might
        get rate limited and have to give up halfway down the participant
        list.</small>
    </p>

    <div>
      <div class="text-small">
        <div v-if="scraperLoading">
          Working on getting the scraper started...
        </div>

        <div v-if="!scraperLoading && scraperRes">
          <p v-if="scraperRes.data && scraperRes.data.status == 'started'">
            This exchange is now being processed! Go get some tea, and refresh
            this page in about 5 - 15 minutes (depending on how large it is).
          </p>

          <p v-if="scraperRes.data && scraperRes.data.status == 'processing'">
            This exchange is already being processed! Wait a lil'.
          </p>

          <p v-if="scraperRes.status == 503">
            The server is currently processing another challenge,
            {{ scraperRes.data.currentlyScraping }}. Try again in 5-15 minutes.
            If it's been a lot longer, please report it via the Report Bug link
            at the top.
          </p>
        </div>
      </div>
    </div>

    <div>Participants scraped: {{ giftsData ? giftsData.length : 0 }}</div>

    <table class="table">
      <thead>
        <th>Username</th>
        <th>Gifts</th>
        <th>Updated At</th>
      </thead>
      <tbody>
        <tr v-for="row in giftsData" :key="row.username">
          <td>
            <router-link :to="`/${exchangeId}/user/${row.username}`">{{
              row.username
            }}</router-link>
          </td>
          <td>
            {{ row.gifts }}
          </td>
          <td>
            {{ new Date(row["updated_at"]).toLocaleString() }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { orderBy } from "lodash-es";

export default {
  name: "gifts",
  data() {
    return {
      loading: false,
      giftsData: [],
      scraperLoading: false,
      scraperRes: undefined
    };
  },
  beforeMount() {
    this.load();
  },
  computed: {
    ...mapGetters(["exchangeId"])
  },
  watch: {
    $route(to, from) {
      if (to.name != "gifts") return;

      if (
        to.name !== from.name ||
        this.exchangeId != this.$route.params.exchangeId
      ) {
        this.$store.commit("setExchangeId", this.$route.params.exchangeId);
        this.load();
      }
    }
  },
  methods: {
    scrape() {
      this.scraperLoading = true;
      axios
        .get(`https://autoao3.herokuapp.com/scrape/${this.exchangeId}/gifts`)
        .then(res => {
          this.scraperRes = res;
          this.scraperLoading = false;
        })
        .catch(e => {
          if (e.response) {
            this.scraperRes = e.response;
            this.scraperLoading = false;
            return;
          }
          this.scraperRes = {
            error: true,
            e: e,
            msg: "Something went wrong! Report a bug."
          };
          this.scraperLoading = false;
        });
    },
    async load() {
      this.$store.commit("setExchangeId", this.$route.params.exchangeId);

      if (!this.exchangeId || !this.exchangeId.length) return;

      this.loading = true;

      const url = `https://autoao3.herokuapp.com/db/exchange/${encodeURIComponent(
        this.exchangeId
      )}/gifts`;

      const data = await axios
        .get(url)
        .then(res => res.data)
        .catch(() => []);

      this.giftsData = orderBy(data, [f => f.gifts, f => f.username.toLowerCase()]);
      this.loading = false;
    }
  }
};
</script>

<style lang="scss">
// Tables
.tagset-table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;

  td,
  th {
    padding: 0.25rem;
    border-bottom: 1px solid #eee;
  }

  th {}
}
</style>
